import React, { useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Select,
  DatePicker,
  Row,
  Col,
  Typography,
  Input,
  Collapse,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  StopOutlined,
  FilterOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  usePurchaseRequisitions,
  useSubmitPurchaseRequisition,
  useApprovePurchaseRequisition,
  useCancelPurchaseRequisition,
} from '@/hooks/usePurchaseRequisition';
import { useCurrency } from '@/contexts/CurrencyContext';
import type { PurchaseRequisition } from '@/services/purchaseRequisition.service';
import { useUserDisplayResolver } from '@/hooks/useUserDisplayResolver';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { ProjectSelector } from '@/components/project/ProjectSelector';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;

const PurchaseRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const { resolve } = useUserDisplayResolver();
  const { formatCurrency } = useCurrency();

  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText.trim(), 300);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([] as any);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [creationModeFilter, setCreationModeFilter] = useState<string>('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [itemDescription, setItemDescription] = useState('');
  const [itemCode, setItemCode] = useState('');
  const [projectId, setProjectId] = useState<string | undefined>();
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [salesOrderNumber, setSalesOrderNumber] = useState('');

  const debouncedItemDescription = useDebouncedValue(itemDescription.trim(), 300);
  const debouncedItemCode = useDebouncedValue(itemCode.trim(), 300);
  const debouncedSoNumber = useDebouncedValue(salesOrderNumber.trim(), 300);

  const advancedActiveCount = useMemo(() => {
    let n = 0;
    if (debouncedItemDescription) n += 1;
    if (debouncedItemCode) n += 1;
    if (projectId) n += 1;
    if (priorityFilter) n += 1;
    if (debouncedSoNumber) n += 1;
    return n;
  }, [debouncedItemDescription, debouncedItemCode, projectId, priorityFilter, debouncedSoNumber]);

  const filters = {
    from_date: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
    to_date: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
    status: statusFilter || undefined,
    creation_mode: creationModeFilter || undefined,
    search: debouncedSearch || undefined,
    item_description: debouncedItemDescription || undefined,
    item_code: debouncedItemCode || undefined,
    project_id: projectId || undefined,
    priority: priorityFilter || undefined,
    sales_order_number: debouncedSoNumber || undefined,
  };
  const { data: list = [], isLoading } = usePurchaseRequisitions(filters);
  const submitMutation = useSubmitPurchaseRequisition();
  const approveMutation = useApprovePurchaseRequisition();
  const cancelMutation = useCancelPurchaseRequisition();

  const clearAllFilters = () => {
    setSearchText('');
    setDateRange([] as any);
    setStatusFilter('');
    setCreationModeFilter('');
    setItemDescription('');
    setItemCode('');
    setProjectId(undefined);
    setPriorityFilter('');
    setSalesOrderNumber('');
  };

  const columns: ColumnsType<PurchaseRequisition> = [
    { title: 'PR No', dataIndex: 'pr_number', key: 'pr_number', width: 130 },
    { title: 'Date', dataIndex: 'pr_date', key: 'pr_date', width: 110, render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY') : '-') },
    { title: 'Mode', dataIndex: 'creation_mode', key: 'creation_mode', width: 120, render: (v: string) => (v === 'AgainstSalesOrder' ? 'From Customer Order' : 'Direct') },
    {
      title: 'Requested By',
      key: 'requested_by',
      width: 140,
      render: (_, r) =>
        (r as any).requestedByUser?.full_name ??
        (r as any).requestedByUser?.email ??
        resolve(r.requested_by, '—'),
    },
    { title: 'Required Date', dataIndex: 'required_date', key: 'required_date', width: 110, render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY') : '-') },
    { title: 'Priority', dataIndex: 'priority', key: 'priority', width: 90 },
    { title: 'Estimated Total', dataIndex: 'estimated_total', key: 'estimated_total', width: 120, align: 'right', render: (v: number, r) => formatCurrency(Number(v), r.currency) },
    {
      title: 'Status',
      dataIndex: 'pr_status',
      key: 'pr_status',
      width: 100,
      render: (s: string) => {
        const color =
          s === 'Approved' ? 'green' : s === 'Rejected' ? 'orange' : s === 'Submitted' ? 'blue' : s === 'Cancelled' ? 'red' : 'default';
        return <Tag color={color}>{s}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      render: (_, record) => {
        const approvalPending = (record.approval_status ?? 'Pending') === 'Pending';
        return (
          <Space size="small">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/scm/procurement/purchase-requests/${record.id}`)}>
              View
            </Button>
            {record.pr_status === 'Draft' && (
              <>
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => navigate(`/scm/procurement/purchase-requests/${record.id}/edit`)}>
                  Edit
                </Button>
                <Button type="link" size="small" icon={<CheckCircleOutlined />} onClick={() => submitMutation.mutate(record.id)} loading={submitMutation.isPending}>
                  Submit
                </Button>
              </>
            )}
            {record.pr_status === 'Submitted' && approvalPending && (
              <Button type="link" size="small" icon={<CheckCircleOutlined />} onClick={() => approveMutation.mutate(record.id)} loading={approveMutation.isPending}>
                Approve
              </Button>
            )}
            {(record.pr_status === 'Draft' || (record.pr_status === 'Submitted' && approvalPending)) && (
              <Button type="link" size="small" danger icon={<StopOutlined />} onClick={() => cancelMutation.mutate(record.id)} loading={cancelMutation.isPending}>
                Cancel
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Purchase Requests
          </Title>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/scm/procurement/purchase-requests/new')}>
            Create Purchase Request
          </Button>
        </Col>
      </Row>
      <Card>
        <Space style={{ marginBottom: 12 }} wrap>
          <Search
            placeholder="Search PR #, item, project, purpose…"
            allowClear
            style={{ width: 280 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={setSearchText}
          />
          <RangePicker value={dateRange} onChange={(v) => setDateRange(v as any)} />
          <Select
            placeholder="Status"
            allowClear
            style={{ width: 120 }}
            value={statusFilter || undefined}
            onChange={(v) => setStatusFilter(v ?? '')}
            options={[
              { value: 'Draft', label: 'Draft' },
              { value: 'Submitted', label: 'Submitted' },
              { value: 'Approved', label: 'Approved' },
              { value: 'Rejected', label: 'Rejected' },
              { value: 'Cancelled', label: 'Cancelled' },
            ]}
          />
          <Select
            placeholder="Creation mode"
            allowClear
            style={{ width: 160 }}
            value={creationModeFilter || undefined}
            onChange={(v) => setCreationModeFilter(v ?? '')}
            options={[
              { value: 'Direct', label: 'Direct' },
              { value: 'AgainstSalesOrder', label: 'From Customer Order' },
            ]}
          />
          <Button
            icon={<FilterOutlined />}
            type={showAdvanced || advancedActiveCount > 0 ? 'primary' : 'default'}
            ghost={showAdvanced || advancedActiveCount > 0}
            onClick={() => setShowAdvanced((p) => !p)}
          >
            Advanced{advancedActiveCount > 0 ? ` (${advancedActiveCount})` : ''}
          </Button>
          <Button icon={<ClearOutlined />} onClick={clearAllFilters}>
            Clear
          </Button>
        </Space>

        {showAdvanced && (
          <Collapse
            defaultActiveKey={['advanced']}
            style={{ marginBottom: 16 }}
            items={[
              {
                key: 'advanced',
                label: (
                  <Text type="secondary">
                    Search line details — item description, item code, project, customer order
                  </Text>
                ),
                children: (
                  <Row gutter={[16, 12]}>
                    <Col xs={24} sm={12} md={8}>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                        Item description
                      </Text>
                      <Input
                        placeholder="Contains…"
                        allowClear
                        value={itemDescription}
                        onChange={(e) => setItemDescription(e.target.value)}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                        Item code
                      </Text>
                      <Input
                        placeholder="Contains…"
                        allowClear
                        value={itemCode}
                        onChange={(e) => setItemCode(e.target.value)}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                        Project
                      </Text>
                      <ProjectSelector
                        value={projectId}
                        onChange={(id) => setProjectId(id)}
                        allowClear
                        placeholder="Header or line project"
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                        Priority
                      </Text>
                      <Select
                        placeholder="Any"
                        allowClear
                        style={{ width: '100%' }}
                        value={priorityFilter || undefined}
                        onChange={(v) => setPriorityFilter(v ?? '')}
                        options={[
                          { value: 'Low', label: 'Low' },
                          { value: 'Normal', label: 'Normal' },
                          { value: 'High', label: 'High' },
                          { value: 'Urgent', label: 'Urgent' },
                        ]}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                        Customer order #
                      </Text>
                      <Input
                        placeholder="Contains…"
                        allowClear
                        value={salesOrderNumber}
                        onChange={(e) => setSalesOrderNumber(e.target.value)}
                      />
                    </Col>
                  </Row>
                ),
              },
            ]}
          />
        )}

        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          loading={isLoading}
          pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default PurchaseRequestsPage;
